import Main from "niteroi/packages/sales-distribution/controllers/Main.controller";
import {SearchField$LiveChangeEvent} from "sap/m/SearchField";
import {Table$RowSelectionChangeEvent} from "sap/ui/table/Table";
import {
    IBusinessPartner,
    IDeliveryNote,
    IDeliveryNoteItem,
    ISalesOrder
} from "niteroi/packages/sales-distribution/models/interfaces";
import JSONModel from "sap/ui/model/json/JSONModel";
import {Route$PatternMatchedEvent} from "sap/ui/core/routing/Route";
import {ListBase$DeleteEvent} from "sap/m/ListBase";
import Input, {Input$SuggestionItemSelectedEvent, Input$ValueHelpRequestEvent} from "sap/m/Input";
import TableSelectDialog, {
    TableSelectDialog$ConfirmEvent,
    TableSelectDialog$SearchEvent
} from "sap/m/TableSelectDialog";
import ColumnListItem from "sap/m/ColumnListItem";
import ListBinding from "sap/ui/model/ListBinding";
import Filter from "sap/ui/model/Filter";
import FilterOperator from "sap/ui/model/FilterOperator";
import Event from "sap/ui/base/Event";
import {ILoadDataResponse} from "niteroi/packages/system/models/interfaces";

export default class Deliveries extends Main {
    private readonly onPatternMatchedHandler = this.onPatternMatched.bind(this);

    public onInit(): void {
        super.onInit();
        const router = this.getRouter();
        const routeName = this.getCurrentRouteName(router);
        if (routeName) {
            const route = router.getRoute(routeName);
            route.detachPatternMatched(this.onPatternMatchedHandler);
            route.attachPatternMatched(this.onPatternMatchedHandler);
        }
        // this.getData();
    }

    public onBeforeRendering(): void {
        super.onBeforeRendering();

        this.initializeDocumentModels();
    }

    private initializeDocumentModels(): void {
        if (!this.getModel("deliveryNotePartner")) {
            this.setModel(new JSONModel({}), "deliveryNotePartner");
        }
    }

    protected onPatternMatched(event: Route$PatternMatchedEvent): void {
        const routeName = event.getParameter("name");

        this.loadCollectionData("business-partners", "salesDistributionBusinessPartnerCollection");
        this.loadCollectionData("orders", "salesDistributionSalesOrderCollection");

        if (routeName === "sales-distribution/deliveries/add") {
            this.getModel("salesDistributionDeliveryDetail").setData(this.getBlankDocumentModel());
            this.getView().setBusy(false);
            return;
        } else {
            const id = (event.getParameter("arguments") as { id?: string })?.id ?? "";
            this.getData(id);
        }
    }

    protected getData(objectId: string = ""): void {
        const componentRoute = "/api/sales-distribution/deliveries";
        const url = objectId ? `${componentRoute}/${objectId}` : componentRoute;
        const headers = this.getRequestHeaders();
        const requestModel = this.createRequestModel();
        requestModel.loadData(url, "", true, "GET", false, true, headers).then(() => {
            const modelData = requestModel.getData();
            const modelName = objectId ? "salesDistributionDeliveryDetail" : "salesDistributionDeliveryCollection";
            const model = this.getModel(modelName);
            model.setData(modelData);
            model.refresh(true);
        }).catch((response: ILoadDataResponse) => {
            const responseText = JSON.parse(response.responseText);
            this.showToast(`${responseText.code} - ${responseText.error}`);
        }).finally(() => {
            this.getView().setBusy(false);
        });
    }

    public onListSearch(event: SearchField$LiveChangeEvent): void {
        this.onSearchList(event, "idDeliveriesTable", ["deliveryNoteNumber", "deliveryNoteId", "status", "plannedDeliveryDate", "goodsIssueDate", "shippingPoint", "salesOrderId", "shipToPartnerId", "createdAt"]);
    }

    protected getBlankDocumentModel(): IDeliveryNote {
        return {
            createdAt: "",
            deliveryNoteId: "",
            deliveryNoteNumber: "",
            goodsIssueDate: "",
            items: [],
            plannedDeliveryDate: "",
            salesOrderId: "",
            shipToPartnerId: "",
            shippingPoint: "",
            status: "OPEN",
            updatedAt: ""

        };
    }

    protected getBlankDeliveryNotePartner(): IBusinessPartner {
        return {
            businessPartnerId: "",
            customerNumber: "",
            legalName1: "",
            legalName2: "",
            vatId: "",
            taxNumber: "",
            partnerRole: "",
            isActive: false,
            createdAt: "",
            updatedAt: "",
            companyId: "",
            addresses: [],
            salesAreas: []
        };
    }

    public onCreateDeliveryPress(): void {
        this.setDocumentModel(this.getBlankDocumentModel(), this.getBlankDeliveryNotePartner());
        this.navTo("sales-distribution/deliveries/add", {}, true);
    }

    public onRefreshDeliveryPress(): void {
        this.getModel("salesDistributionDeliveryCollection")?.refresh(true);
    }

    public onRowSelectionChange(event: Table$RowSelectionChangeEvent): void {
        const rowContext = event.getParameter("rowContext");
        const selectedDocument = rowContext?.getObject() as IDeliveryNote | undefined;

        if (!selectedDocument?.deliveryNoteId) {
            return;
        }


        let partner = this.getBlankDeliveryNotePartner();
        if (selectedDocument.shipToPartnerId) {
            const businessPartnersModel = this.getModel("salesDistributionBusinessPartnerCollection");
            const businessPartners = (businessPartnersModel?.getData() as IBusinessPartner[]) ?? [];
            partner = businessPartners.find((p) => p.businessPartnerId === selectedDocument.shipToPartnerId) ?? partner;
        }

        this.setDocumentModel(selectedDocument, partner);
        this.navTo("sales-distribution/deliveries/edit", {id: selectedDocument.deliveryNoteId}, false);
    }

    protected setDocumentModel(document: IDeliveryNote, partner: IBusinessPartner): void {
        let deliveryNoteModel = this.getModel("salesDistributionDeliveryDetail");
        if (!deliveryNoteModel) {
            deliveryNoteModel = new JSONModel({});
            this.setModel(deliveryNoteModel, "salesDistributionDeliveryDetail");
        }
        deliveryNoteModel.setData(document);

        let deliveryNotePartnerModel = this.getModel("deliveryNotePartner");
        if (!deliveryNotePartnerModel) {
            deliveryNotePartnerModel = new JSONModel({});
            this.setModel(deliveryNotePartnerModel, "deliveryNotePartner");
        }
        deliveryNotePartnerModel.setData(partner);
    }

    public onNavButtonPress(): void {
        this.navTo("sales-distribution/deliveries", {}, false);
    }

    public onPressSaveDraft(): void {
        const model = this.getModel("salesDistributionDeliveryDetail");
        model.setProperty("/status", "OPEN");
        model.setProperty("/updatedAt", new Date().toISOString().replace("T", " ").split(".")[0]);
        this.persist();
    }

    public onPressViewDocument(): void {
    }

    public onPressCancelButton(): void {
        const model = this.getModel("salesDistributionDeliveryDetail");
        model.setProperty("/status", "CANCELLED");
        model.setProperty("/updatedAt", new Date().toISOString().replace("T", " ").split(".")[0]);
        this.persist();
    }

    public onPressPostGoodsIssue(): void {
        const model = this.getModel("salesDistributionDeliveryDetail");
        model.setProperty("/goodsIssueDate", new Date().toISOString().split("T")[0]);
        model.setProperty("/status", "SHIPPED");
        model.setProperty("/updatedAt", new Date().toISOString().replace("T", " ").split(".")[0]);
        this.persist();
    }

    public onPressCompleteAndInvoice(): void {
        const model = this.getModel("salesDistributionDeliveryDetail");
        model.setProperty("/status", "COMPLETED");
        model.setProperty("/updatedAt", new Date().toISOString().replace("T", " ").split(".")[0]);
        this.persist();
    }

    public onPressSaveDocument(): void {
        const deliveryNoteModel = this.getModel("salesDistributionDeliveryDetail");

        // check all line items
        const deliveryNoteItems = deliveryNoteModel.getProperty("/items") as Array<IDeliveryNoteItem>;
        if (deliveryNoteItems.length === 0) {
            return;
        } else if (deliveryNoteItems.length > 0) {
            const validItems = deliveryNoteItems.filter((item) => item.materialNumber && item.deliveredQuantity && item.itemNumber && item.salesOrderItemId && item.unit);
            if (validItems.length === 0) {
                return;
            }
            deliveryNoteModel.setProperty("/items", validItems);
        }

        const shipToPartnerId = deliveryNoteModel.getProperty("/shipToPartnerId");
        if (!shipToPartnerId) {
            return;
        }

        deliveryNoteModel.setProperty("/status", "OPEN");
        const now = new Date().toISOString().replace("T", " ").split(".")[0]; // YYYY-MM-DD HH:mm:ss
        if (!deliveryNoteModel.getProperty("/createdAt")) {
            deliveryNoteModel.setProperty("/createdAt", now);
        }
        deliveryNoteModel.setProperty("/updatedAt", now);

        this.persist();
    }

    private persist(): void {
        this.getView().setBusy(true);

        const deliveryNoteModel = this.getModel("salesDistributionDeliveryDetail");
        const deliveryNoteId = deliveryNoteModel.getProperty("/deliveryNoteId") as string;

        if (!deliveryNoteModel.getProperty("/shipToPartnerId")) {
            this.getView().setBusy(false);
            return;
        }

        const componentRoute = "/api/sales-distribution/deliveries";
        const url = deliveryNoteId ? `${componentRoute}/${deliveryNoteId}` : componentRoute;
        const method = deliveryNoteId ? "PUT" : "POST";
        deliveryNoteModel.loadData(url, deliveryNoteModel.getJSON(), true, method, false, true, this.getRequestHeaders()).then(() => {
            if (method === "POST") {
                this.navTo("sales-distribution/deliveries/edit", {id: deliveryNoteModel.getProperty("/deliveryNoteId")}, false);
            }
        }).catch((response: ILoadDataResponse) => {
            const responseText = JSON.parse(response.responseText);
            this.showToast(`${responseText.code} - ${responseText.error}`);
        }).finally(() => {
            this.getView().setBusy(false);
        });
    }

    public onPressAddItem(): void {
        const model = this.getModel("salesDistributionDeliveryDetail");
        const modelData = model.getData() as IDeliveryNote;
        const lastIndex = modelData.items.slice(-1);
        const nextItemNumber = lastIndex[0] ? (lastIndex[0].itemNumber + 10) : 10;
        const newItem: IDeliveryNoteItem = {
            deliveredQuantity: "",
            deliveryNoteId: "",
            deliveryNoteItemId: "",
            description: "",
            itemNumber: nextItemNumber,
            materialNumber: "",
            salesOrderItemId: "",
            storageLocation: "",
            unit: ""
        };
        modelData.items.push(newItem);
        model.refresh(true);
    }

    public onDeleteItem(event: ListBase$DeleteEvent): void {
        const source = event.getParameter("listItem");
        const bindingContext = source.getBindingContext("salesDistributionDeliveryDetail");
        const model = bindingContext.getModel() as JSONModel;
        const deliveryNoteItemId = model.getProperty(bindingContext.getPath()).deliveryNoteItemId;
        const modelData = model.getData() as IDeliveryNote;
        const itemIndex = modelData.items.findIndex((deliveryNoteItem) => deliveryNoteItem.deliveryNoteItemId === deliveryNoteItemId);
        if (itemIndex > -1) {
            modelData.items.splice(itemIndex, 1);
            model.refresh(true);
        }
    }

    protected _updatePartner(partner: IBusinessPartner): void {
        const deliveryNoteModel = this.getView()?.getModel("salesDistributionDeliveryDetail") as JSONModel;
        if (deliveryNoteModel) {
            deliveryNoteModel.setProperty("/shipToPartnerId", partner.businessPartnerId);
            this.getModel("deliveryNotePartner").setData(partner);
        }
    }

    public onPressSalesOrderNumber(): void {
        this.getModel("salesDistributionDeliveryDetail").setProperty("/salesOrderId", "");
        (this.byId("salesOrderIdInput") as Input).fireValueHelpRequest();
    }

    /**
     * Opens the Sales Order Item Value Help Dialog
     */
    public onPressAddFromSalesOrder(event: Input$ValueHelpRequestEvent): void {
        const dialog = this.byId("salesOrderItemValueHelpDialog") as TableSelectDialog;
        if (dialog) {
            dialog.open(event.getSource().getValue());
        }
    }

    /**
     * Handles search in the Sales Order Item Value Help Dialog
     */
    public onSalesOrderItemValueHelpSearch(event: TableSelectDialog$SearchEvent): void {
        const value = event.getParameter("value") || "";
        const dialog = event.getSource() as TableSelectDialog;
        const binding = dialog.getBinding("items") as ListBinding;

        // Filter for IN_PROCESS sales orders only
        const inProcessFilter = new Filter("status", FilterOperator.EQ, "IN_PROCESS");

        if (!value) {
            binding.filter([inProcessFilter]);
            return;
        }

        const searchFilters = [
            new Filter("salesOrderNumber", FilterOperator.Contains, value),
            new Filter("soldToPartnerId", FilterOperator.Contains, value),
            new Filter("items/0/description", FilterOperator.Contains, value),
            new Filter("items/0/materialNumber", FilterOperator.Contains, value)
        ];

        const combinedSearchFilter = new Filter({filters: searchFilters, and: false});
        const finalFilter = new Filter({filters: [inProcessFilter, combinedSearchFilter], and: true});

        binding.filter([finalFilter]);
    }

    /**
     * Handles confirmation of Sales Order selection
     */
    public onSalesOrderItemValueHelpConfirm(event: TableSelectDialog$ConfirmEvent): void {
        const selectedItems = event.getParameter("selectedItems");
        if (!selectedItems || selectedItems.length === 0) {
            this._resetSalesOrderItemValueHelpDialogFilters(event);
            return;
        }

        const context = selectedItems[0].getBindingContext("salesDistributionSalesOrderCollection");
        if (!context) {
            this._resetSalesOrderItemValueHelpDialogFilters(event);
            return;
        }

        const salesOrder = context.getObject() as ISalesOrder;
        this._updateDeliveryNoteSalesOrder(salesOrder);
    }

    /**
     * Cleans up filters if the user cancels the sales order item dialog
     */
    public onSalesOrderItemValueHelpCancel(event: Event): void {
        this._resetSalesOrderItemValueHelpDialogFilters(event);
    }

    /**
     * Internal helper to reset sales order item dialog filters
     */
    private _resetSalesOrderItemValueHelpDialogFilters(event: Event): void {
        const dialog = event.getSource() as TableSelectDialog;
        const binding = dialog.getBinding("items") as ListBinding;
        if (binding) {
            binding.filter([]);
        }
    }

    /**
     * Updates delivery note with sales order data
     */
    private _updateDeliveryNoteSalesOrder(salesOrder: ISalesOrder): void {
        const deliveryNoteModel = this.getView()?.getModel("salesDistributionDeliveryDetail") as JSONModel;
        if (deliveryNoteModel) {
            deliveryNoteModel.setProperty("/salesOrderId", salesOrder.salesOrderId);
            deliveryNoteModel.setProperty("/shipToPartnerId", salesOrder.soldToPartnerId);

            // Copy sales order items to delivery note items
            const newItems: IDeliveryNoteItem[] = [];
            salesOrder.items.forEach((salesOrderItem, index) => {
                const newItem: IDeliveryNoteItem = {
                    deliveryNoteItemId: "",
                    deliveryNoteId: "",
                    itemNumber: (index + 1) * 10,
                    materialNumber: salesOrderItem.materialNumber,
                    description: salesOrderItem.description,
                    salesOrderItemId: salesOrderItem.salesOrderItemId,
                    deliveredQuantity: salesOrderItem.quantity,
                    unit: salesOrderItem.unit,
                    storageLocation: ""
                };
                newItems.push(newItem);
            });

            deliveryNoteModel.setProperty("/items", newItems);

            // Update sales order detail model
            const salesOrderModel = this.getModel("salesDistributionSalesOrderDetail");
            if (!salesOrderModel) {
                this.setModel(new JSONModel({}), "salesDistributionSalesOrderDetail");
            }
            this.getModel("salesDistributionSalesOrderDetail").setData(salesOrder);

            // Update partner detail
            const partnerModel = this.getModel("salesDistributionBusinessPartnerCollection");
            const partner = partnerModel.getData().find((partner: IBusinessPartner) => {
                return partner.businessPartnerId === salesOrder.soldToPartnerId;
            });
            if (partner) {
                this.getModel("deliveryNotePartner").setData(partner);
            }
        }
    }

    /**
     * Handles suggestion item selection for sales orders
     */
    public onSuggestionItemSelectedSalesOrder(event: Input$SuggestionItemSelectedEvent): void {
        const selectedItem = event.getParameter("selectedRow") as ColumnListItem;
        const bindingContext = selectedItem.getBindingContext("salesDistributionSalesOrderCollection");
        if (bindingContext) {
            const salesOrder = bindingContext.getObject() as ISalesOrder;
            this._updateDeliveryNoteSalesOrder(salesOrder);
        }
    }
}
